import { useRef, useState } from "react";
import { landerV2TrustpilotReviews } from "./trustpilotReviews";
import { ReviewInitialsAvatar } from "./ReviewInitialsAvatar";
import { ReviewStars } from "./ReviewStars";
import { LanderV2TrustpilotReview } from "./trustpilotReviews";

type ReviewerAvatarPosition = "left" | "top";

type TrustpilotReviewsCarouselProps = {
  mode?: "grid" | "single";
  reviewerAvatarPosition?: ReviewerAvatarPosition;
};

function TrustpilotReviewCard({
  review,
  reviewerAvatarPosition,
}: {
  review: LanderV2TrustpilotReview;
  reviewerAvatarPosition: ReviewerAvatarPosition;
}) {
  return (
    <figure className={`pa-vb-review pa-vb-review-avatar-${reviewerAvatarPosition}`}>
      <ReviewStars rating={5} />
      <blockquote>
        <span className="ux2-text-review">{review.content}</span>
      </blockquote>
      <figcaption>
        <ReviewInitialsAvatar review={review} />
        <span>
          <strong>{review.reviewer}</strong>
          <br />
          {review.dateLabel}
        </span>
      </figcaption>
    </figure>
  );
}

export function TrustpilotReviewsCarousel({
  mode = "grid",
  reviewerAvatarPosition = "left",
}: TrustpilotReviewsCarouselProps) {
  const reviewTrackRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const hasScrollableReviews = landerV2TrustpilotReviews.length > 1;

  const scrollReviews = (direction: "previous" | "next") => {
    const reviewTrack = reviewTrackRef.current;
    if (!reviewTrack) return;

    const scrollDistance = reviewTrack.clientWidth * 0.85;
    reviewTrack.scrollBy({
      left: direction === "next" ? scrollDistance : -scrollDistance,
      behavior: "smooth",
    });
  };

  const moveSingleReview = (direction: "previous" | "next") => {
    setActiveIndex((currentIndex) => {
      const delta = direction === "next" ? 1 : -1;
      return (currentIndex + delta + landerV2TrustpilotReviews.length) % landerV2TrustpilotReviews.length;
    });
  };

  if (mode === "single") {
    const review = landerV2TrustpilotReviews[activeIndex];

    return (
      <div className="pa-vb-review-carousel pa-vb-review-carousel-single">
        {hasScrollableReviews && (
          <button type="button" onClick={() => moveSingleReview("previous")} aria-label="Previous review">
            <i className="bi bi-chevron-left" aria-hidden="true"></i>
          </button>
        )}
        <TrustpilotReviewCard review={review} reviewerAvatarPosition={reviewerAvatarPosition} />
        {hasScrollableReviews && (
          <button type="button" onClick={() => moveSingleReview("next")} aria-label="Next review">
            <i className="bi bi-chevron-right" aria-hidden="true"></i>
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="pa-vb-review-carousel">
      {hasScrollableReviews && (
        <button type="button" onClick={() => scrollReviews("previous")} aria-label="Previous reviews">
          <i className="bi bi-chevron-left" aria-hidden="true"></i>
        </button>
      )}
      <div className="pa-vb-review-grid" ref={reviewTrackRef}>
        {landerV2TrustpilotReviews.map((review) => (
          <TrustpilotReviewCard
            key={review.reviewer}
            review={review}
            reviewerAvatarPosition={reviewerAvatarPosition}
          />
        ))}
      </div>
      {hasScrollableReviews && (
        <button type="button" onClick={() => scrollReviews("next")} aria-label="Next reviews">
          <i className="bi bi-chevron-right" aria-hidden="true"></i>
        </button>
      )}
    </div>
  );
}
